import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  CloudArrowUpIcon, DocumentTextIcon, SparklesIcon,
  ExclamationCircleIcon, PhotoIcon,
} from '@heroicons/react/24/outline';

const LANGUAGES = [
  { code: 'en', label: 'English' }, { code: 'es', label: 'Spanish (Español)' },
  { code: 'fr', label: 'French (Français)' }, { code: 'ht', label: 'Haitian Creole (Kreyòl Ayisyen)' },
  { code: 'vi', label: 'Vietnamese (Tiếng Việt)' }, { code: 'ar', label: 'Arabic (العربية)' },
  { code: 'zh', label: 'Chinese Simplified (中文)' }, { code: 'pt', label: 'Portuguese (Português)' },
  { code: 'ko', label: 'Korean (한국어)' }, { code: 'ru', label: 'Russian (Русский)' },
  { code: 'hi', label: 'Hindi (हिन्दी)' }, { code: 'tl', label: 'Tagalog' },
];

const LITERACY_LEVELS = [
  { value: 'low', label: 'Simple', description: 'Short sentences, everyday words, no medical jargon' },
  { value: 'medium', label: 'Standard', description: 'Plain language with brief explanations of medical terms' },
  { value: 'high', label: 'Detailed', description: 'More detail, standard medical terms explained' },
  { value: 'professional', label: 'Clinical', description: 'Full clinical language, for healthcare professionals' },
];

const AI_CONSENT_KEY = 'hcai_ai_consent_given';

export default function UploadPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [rawText, setRawText] = useState('');
  const [inputMode, setInputMode] = useState('file'); // file | photo | paste
  const [language, setLanguage] = useState(user?.preferredLanguage || 'en');
  const [literacyLevel, setLiteracyLevel] = useState(user?.healthLiteracyLevel || 'medium');

  // Sync language and literacy level when user profile changes (e.g. after saving Profile & Settings)
  useEffect(() => {
    if (user?.preferredLanguage) setLanguage(user.preferredLanguage);
  }, [user?.preferredLanguage]);

  useEffect(() => {
    if (user?.healthLiteracyLevel) setLiteracyLevel(user.healthLiteracyLevel);
  }, [user?.healthLiteracyLevel]);
  const [reportDate, setReportDate] = useState('');
  const [labProvider, setLabProvider] = useState('');
  const [status, setStatus] = useState(null); // null | uploading | analyzing | error
  const [error, setError] = useState('');
  const [aiConsent, setAiConsent] = useState(() => localStorage.getItem(AI_CONSENT_KEY) === '1');

  const onDrop = useCallback((accepted) => {
    if (accepted[0]) { setFile(accepted[0]); setError(''); }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'], 'text/plain': ['.txt'] },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024,
    onDropRejected: () => setError('File must be a PDF or .txt under 10MB'),
  });

  const onDropPhoto = useCallback((accepted) => {
    if (accepted[0]) { setFile(accepted[0]); setError(''); }
  }, []);

  const { getRootProps: getPhotoRootProps, getInputProps: getPhotoInputProps, isDragActive: isPhotoDragActive } = useDropzone({
    onDrop: onDropPhoto,
    accept: { 'image/*': [] },
    maxFiles: 1,
    maxSize: 20 * 1024 * 1024,
    onDropRejected: () => setError('Photo must be an image file under 20MB'),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (inputMode === 'file' && !file) return setError('Please select a file');
    if (inputMode === 'photo' && !file) return setError('Please select a photo');
    if (inputMode === 'paste' && !rawText.trim()) return setError('Please paste your lab results');
    if (!aiConsent) {
      return setError('Please acknowledge the AI analysis disclosure before continuing.');
    }

    try {
      setStatus('uploading');
      setError('');

      // Step 1: Upload report
      let reportId;
      if (inputMode === 'file' || inputMode === 'photo') {
        const formData = new FormData();
        formData.append('file', file);
        if (reportDate) formData.append('reportDate', reportDate);
        if (labProvider) formData.append('labProvider', labProvider);
        const uploadRes = await api.post('/reports/upload', formData);
        reportId = uploadRes.data.reportId;
      } else {
        const uploadRes = await api.post('/reports/upload', {
          text: rawText,
          reportDate: reportDate || undefined,
          labProvider: labProvider || undefined,
        });
        reportId = uploadRes.data.reportId;
      }

      // Step 2: Run analysis
      setStatus('analyzing');
      const analysisRes = await api.post('/analysis/start', { reportId, language, literacyLevel });

      navigate(`/app/analysis/${analysisRes.data.sessionId}`, { state: analysisRes.data });
    } catch (err) {
      if (err.response?.data?.code === 'STARTER_LIMIT_REACHED') {
        setError('STARTER_LIMIT_REACHED');
      } else {
        setError(err.response?.data?.error || 'Something went wrong. Please try again.');
      }
      setStatus(null);
    }
  };

  const isLoading = status === 'uploading' || status === 'analyzing';

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-800 dark:text-white">Upload Lab Report</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">Upload your lab results and we'll explain them in plain language — in your language, at your level.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Input mode tabs */}
        <div className="flex rounded-lg bg-slate-100 dark:bg-slate-800 p-1 gap-1">
          {[['file', 'Upload File'], ['photo', 'Photo'], ['paste', 'Paste Text']].map(([mode, label]) => (
            <button
              key={mode}
              type="button"
              onClick={() => { setInputMode(mode); setFile(null); }}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                inputMode === mode ? 'bg-white dark:bg-slate-700 text-slate-800 dark:text-white shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* File upload */}
        {inputMode === 'file' && (
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
              isDragActive ? 'border-sky-400 bg-sky-50 dark:bg-sky-900/20' : file ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-900/20' : 'border-slate-300 dark:border-slate-600 hover:border-sky-300 hover:bg-sky-50/30 dark:hover:bg-sky-900/10 dark:bg-slate-800/50'
            }`}
          >
            <input {...getInputProps()} />
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <DocumentTextIcon className="w-8 h-8 text-emerald-500" />
                <div className="text-left">
                  <p className="font-medium text-slate-700 dark:text-slate-200">{file.name}</p>
                  <p className="text-sm text-slate-400 dark:text-slate-500">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
              </div>
            ) : (
              <>
                <CloudArrowUpIcon className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                <p className="text-slate-600 dark:text-slate-300 font-medium">Drop your lab report here</p>
                <p className="text-slate-400 dark:text-slate-500 text-sm mt-1">or click to browse · PDF or TXT · max 10MB</p>
              </>
            )}
          </div>
        )}

        {/* Photo upload */}
        {inputMode === 'photo' && (
          <div
            {...getPhotoRootProps()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
              isPhotoDragActive ? 'border-sky-400 bg-sky-50 dark:bg-sky-900/20' : file ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-900/20' : 'border-slate-300 dark:border-slate-600 hover:border-sky-300 hover:bg-sky-50/30 dark:hover:bg-sky-900/10 dark:bg-slate-800/50'
            }`}
          >
            <input {...getPhotoInputProps()} />
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <PhotoIcon className="w-8 h-8 text-emerald-500" />
                <div className="text-left">
                  <p className="font-medium text-slate-700 dark:text-slate-200">{file.name}</p>
                  <p className="text-sm text-slate-400 dark:text-slate-500">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
                {file.type.startsWith('image/') && (
                  <img
                    src={URL.createObjectURL(file)}
                    alt="preview"
                    className="w-16 h-16 rounded-lg object-cover border border-slate-200 dark:border-slate-600 ml-2"
                  />
                )}
              </div>
            ) : (
              <>
                <PhotoIcon className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                <p className="text-slate-600 dark:text-slate-300 font-medium">Take a photo or upload an image</p>
                <p className="text-slate-400 dark:text-slate-500 text-sm mt-1">Any image type · max 20MB</p>
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-2">AI will read the text from your lab results photo</p>
              </>
            )}
          </div>
        )}

        {/* Paste text */}
        {inputMode === 'paste' && (
          <textarea
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
            placeholder="Paste your lab results here…"
            rows={10}
            className="w-full border border-slate-300 dark:border-slate-600 rounded-xl px-4 py-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-sky-400 resize-none bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500"
          />
        )}

        {/* Metadata */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Report Date</label>
            <input
              type="date"
              value={reportDate}
              onChange={(e) => setReportDate(e.target.value)}
              className="w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Lab / Provider (optional)</label>
            <input
              type="text"
              value={labProvider}
              onChange={(e) => setLabProvider(e.target.value)}
              placeholder="e.g. Quest Diagnostics"
              className="w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500"
            />
          </div>
        </div>

        {/* Language */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">Explanation Language</label>
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
          >
            {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
        </div>

        {/* Literacy level */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">Explanation Detail Level</label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {LITERACY_LEVELS.map((lvl) => (
              <button
                key={lvl.value}
                type="button"
                onClick={() => setLiteracyLevel(lvl.value)}
                className={`p-3 rounded-lg border text-left transition-colors ${
                  literacyLevel === lvl.value
                    ? 'border-sky-500 bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300'
                    : 'border-slate-200 dark:border-slate-600 hover:border-slate-300 text-slate-600 dark:text-slate-300 dark:bg-slate-800'
                }`}
              >
                <p className="font-medium text-sm">{lvl.label}</p>
                <p className="text-xs mt-0.5 opacity-70">{lvl.description}</p>
              </button>
            ))}
          </div>
        </div>

        {/* AI disclosure and consent */}
        <div className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
          <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-1">AI analysis disclosure</p>
          <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed mb-3">
            To analyze your report, HealthClear AI securely sends uploaded report data to OpenAI's API.
            Data is encrypted in transit, is not sold to third parties, and is not used for advertising
            tracking. See our <Link to="/privacy" className="text-sky-600 underline">Privacy Policy</Link>
            {' '}for details.
          </p>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={aiConsent}
              onChange={(e) => {
                const value = e.target.checked;
                setAiConsent(value);
                if (value) {
                  localStorage.setItem(AI_CONSENT_KEY, '1');
                } else {
                  localStorage.removeItem(AI_CONSENT_KEY);
                }
              }}
              className="mt-0.5 h-4 w-4 rounded border-slate-300 dark:border-slate-600 text-sky-600 focus:ring-sky-400"
            />
            <span className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
              I understand and consent to AI processing of this upload.
            </span>
          </label>
        </div>

        {/* Error */}
        {error && error === 'STARTER_LIMIT_REACHED' ? (
          <div className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4 text-center">
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1">Monthly upload limit reached</p>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">The Starter plan includes 2 uploads per month. Upgrade for unlimited uploads.</p>
            <Link
              to="/pricing"
              className="inline-block text-sm bg-sky-600 text-white px-5 py-2 rounded-lg font-medium hover:bg-sky-700 transition-colors"
            >
              Upgrade to Full Access — $25/mo
            </Link>
          </div>
        ) : error ? (
          <div className="flex items-center gap-2 text-red-600 bg-red-50 rounded-lg px-4 py-3 text-sm">
            <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />
            {error}
          </div>
        ) : null}

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-sky-600 hover:bg-sky-700 disabled:bg-sky-300 text-white font-semibold py-3 px-6 rounded-xl transition-colors flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
              {status === 'uploading' ? 'Uploading…' : 'Analyzing with AI…'}
            </>
          ) : (
            <>
              <SparklesIcon className="w-5 h-5" />
              Analyze My Report
            </>
          )}
        </button>

        {status === 'analyzing' && (
          <p className="text-center text-sm text-slate-400 animate-pulse">
            AI is extracting your results, computing trends, and personalizing your explanation… this may take 15-30 seconds.
          </p>
        )}
      </form>
    </div>
  );
}
